import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import dayjs, { Dayjs } from "dayjs";
import { useQuery } from "@tanstack/react-query";
import useAuth from "../../../hooks/useAuth";
import { fetchMissedPreTripInspectionStreaks } from "../../../api/preTripInspectionCheckService";
import { PreTripInspectionMissedStreakDto } from "../../../interfaces/Safety/IPreTripInspectionMissedStreak";

const DEFAULT_CONSECUTIVE_DAYS = 1;

const formatDateLabel = (value: Dayjs | null) =>
  value ? value.format("MMMM D, YYYY") : "";

const buildCopyPayload = (
  filters: {
    minimumConsecutiveDays: number;
  },
  streaks: PreTripInspectionMissedStreakDto[]
) => {
  const headers = [
    "#",
    "Driver",
    "Truck",
    "Dispatcher",
    "Last inspection",
  ];

  const rows = streaks.map((streak, index) => [
    `${index + 1}`,
    streak.driverName?.trim() || "Unknown driver",
    streak.truckUnitNumber?.trim() || "Unknown truck",
    streak.dispatcherName?.trim() || "Unknown dispatcher",
    formatDateLabel(dayjs(streak.latestInspectionDate)),
  ]);

  const headerHtml = headers
    .map(
      (header) =>
        `<th style="border:1px solid #ccc;padding:6px 10px;text-align:left;white-space:nowrap;">${header}</th>`
    )
    .join("");

  const bodyHtml = rows
    .map(
      (row) =>
        `<tr>${row
          .map(
            (cell) =>
              `<td style="border:1px solid #ccc;padding:6px 10px;text-align:left;white-space:nowrap;">${cell}</td>`
          )
          .join("")}</tr>`
    )
    .join("");

  const htmlTable = `
<table style="border-collapse:collapse;font-family:Arial,Helvetica,sans-serif;font-size:12px;">
  <thead>
    <tr>${headerHtml}</tr>
  </thead>
  <tbody>
    ${bodyHtml}
  </tbody>
</table>`;

  const tsvLines = [headers.join("\t"), ...rows.map((row) => row.join("\t"))];
  const plainText = `Missed pre-trip inspections\nMinimum consecutive days: ${
    filters.minimumConsecutiveDays
  }\n\n${tsvLines.join("\n")}`;

  return {
    html: `<div><strong>Missed pre-trip inspections (Minimum consecutive days: ${filters.minimumConsecutiveDays})</strong><br/>${htmlTable}</div>`,
    text: plainText,
  };
};

const PreTripInspectionMissedStreaks = () => {
  const { token } = useAuth();
  const [minimumConsecutiveDays, setMinimumConsecutiveDays] = useState<number>(
    DEFAULT_CONSECUTIVE_DAYS
  );
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({ open: false, message: "", severity: "success" });

  const { data, isLoading, isError, refetch, error, isFetching } = useQuery({
    queryKey: ["missedPreTripStreaks", minimumConsecutiveDays],
    queryFn: () =>
      fetchMissedPreTripInspectionStreaks(token, minimumConsecutiveDays),
    enabled: Boolean(token),
  });

  const streaks = data || [];

  useEffect(() => {
    if (minimumConsecutiveDays < 1) {
      setMinimumConsecutiveDays(DEFAULT_CONSECUTIVE_DAYS);
    }
  }, [minimumConsecutiveDays]);

  const handleCopy = async () => {
    if (!streaks.length) {
      setSnackbar({
        open: true,
        message:
          "There are no missed streaks to copy with the selected filters.",
        severity: "info",
      });
      return;
    }

    const payload = buildCopyPayload(
      {
        minimumConsecutiveDays,
      },
      streaks
    );

    try {
      if (navigator.clipboard && "write" in navigator.clipboard) {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([payload.html], { type: "text/html" }),
            "text/plain": new Blob([payload.text], { type: "text/plain" }),
          }),
        ]);
      } else if (navigator.clipboard && "writeText" in navigator.clipboard) {
        await (navigator.clipboard as Clipboard).writeText(payload.text);
      }

      setSnackbar({
        open: true,
        message: "Missed pre-trip list copied to your clipboard.",
        severity: "success",
      });
    } catch (copyError) {
      setSnackbar({
        open: true,
        message: "Unable to copy the list. Please try again.",
        severity: "error",
      });
    }
  };

  return (
    <>
      <Stack spacing={3}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Missed Pre-Trips
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review consecutive missed pre-trip inspections and copy the
              results.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Copy list">
              <span>
                <IconButton
                  color="primary"
                  onClick={handleCopy}
                  disabled={!streaks.length}
                >
                  <ContentCopyRoundedIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Refresh">
              <span>
                <IconButton
                  color="primary"
                  onClick={() => refetch()}
                  disabled={isFetching}
                >
                  <RefreshRoundedIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        <Card elevation={2}>
          <CardContent>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", md: "center" }}
            >
              <TextField
                label="Minimum consecutive days"
                type="number"
                value={minimumConsecutiveDays}
                onChange={(event) =>
                  setMinimumConsecutiveDays(
                    Math.max(1, Number(event.target.value))
                  )
                }
                inputProps={{ min: 1 }}
                fullWidth
              />
              <Button
                variant="contained"
                onClick={() => refetch()}
                disabled={isFetching}
              >
                Apply
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {isLoading || isFetching ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            mt={4}
          >
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Alert severity="error">
            {error instanceof Error
              ? error.message
              : "Something went wrong while loading missed pre-trips."}
          </Alert>
        ) : !streaks.length ? (
          <Alert severity="info">
            No missed pre-trip streaks found for your filters.
          </Alert>
        ) : (
          <TableContainer component={Card} elevation={2}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Driver</TableCell>
                  <TableCell>Truck</TableCell>
                  <TableCell>Dispatcher</TableCell>
                  <TableCell>Last inspection</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {streaks.map((streak, index) => (
                  <TableRow
                    key={`${streak.driverId}-${streak.latestInspectionDate}-${index}`}
                    hover
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{streak.driverName || "Unknown"}</TableCell>
                    <TableCell>{streak.truckUnitNumber || "Unknown"}</TableCell>
                    <TableCell>{streak.dispatcherName || "Unknown"}</TableCell>
                    <TableCell>
                      {dayjs(streak.latestInspectionDate).format("MMM D, YYYY")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PreTripInspectionMissedStreaks;
